import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, isAbortError } from '../services/api';
import { queryKeys } from '../hooks/useApi';
import { useAuth } from './AuthContext';
import type { FormData, GenerateResponse, GenerationJob } from '../types';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------
export interface GenerationJobsContextType {
  jobs: Map<string, GenerationJob>;
  activeJobId: string | null;
  isLoadingJobs: boolean;
  isLoadingMore: boolean;
  hasActiveJobs: boolean;
  hasMoreJobs: boolean;
  totalJobs: number;

  /** Start a new generation. Returns the job id. */
  startGeneration: (formData: FormData) => Promise<string>;
  getJob: (jobId: string) => GenerationJob | undefined;
  removeJob: (jobId: string) => void;
  cancelJob: (jobId: string) => void;
  setActiveJob: (jobId: string | null) => void;
  loadMoreJobs: () => Promise<void>;
}

const GenerationJobsContext = createContext<GenerationJobsContextType | null>(null);

// Client-side progress messages (same as original b135d0c implementation)
const progressMessages = [
  'Initializing generation...',
  'Analyzing business information...',
  'Creating website structure...',
  'Generating homepage...',
  'Building services page...',
  'Writing about page...',
  'Creating contact page...',
  'Generating privacy policy...',
  'Writing terms of service...',
  'Creating cookie policy...',
  'Adding consent management...',
  'Finalizing styles...',
  'Running compliance checks...',
  'Packaging files...',
  'Almost done...',
];

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export const GenerationJobsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Core state
  const [jobs, setJobs] = useState<Map<string, GenerationJob>>(new Map());
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [hasMoreJobs, setHasMoreJobs] = useState(false);

  // Refs for cleanup
  const pollTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const loadedRef = useRef(false);

  // Track user ID to detect user changes and reset state
  const previousUserIdRef = useRef<string | null>(null);

  // Client-side progress simulation intervals (one per job)
  const progressIntervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  // AbortController refs -- one per polling session, plus one for the initial load
  const pollAbortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const loadAbortControllerRef = useRef<AbortController | null>(null);

  // ------ helpers ------

  const updateJob = useCallback((jobId: string, updates: Partial<GenerationJob>) => {
    setJobs((prev) => {
      const next = new Map(prev);
      const job = next.get(jobId);
      if (job) {
        next.set(jobId, { ...job, ...updates });
      }
      return next;
    });
  }, []);

  const clearJobTimers = useCallback((jobId: string) => {
    const timeout = pollTimeoutsRef.current.get(jobId);
    if (timeout) {
      clearTimeout(timeout);
      pollTimeoutsRef.current.delete(jobId);
    }
    // Clear client-side progress simulation
    const progressInterval = progressIntervalsRef.current.get(jobId);
    if (progressInterval) {
      clearInterval(progressInterval);
      progressIntervalsRef.current.delete(jobId);
    }
    // Abort any in-flight fetch requests for this job
    const controller = pollAbortControllersRef.current.get(jobId);
    if (controller) {
      controller.abort();
      pollAbortControllersRef.current.delete(jobId);
    }
  }, []);

  // ------ client-side progress simulation ------
  // Mimics the original b135d0c behavior: random increments every 2s,
  // capped at 95%. Messages cycle based on progress percentage.

  const startProgressSimulation = useCallback(
    (sessionId: string) => {
      const interval = setInterval(() => {
        setJobs((prev) => {
          const next = new Map(prev);
          const job = next.get(sessionId);
          if (job && job.status === 'generating' && job.progress < 95) {
            const newProgress = Math.min(job.progress + Math.random() * 5, 95);
            const messageIndex = Math.min(
              Math.floor(newProgress / (100 / progressMessages.length)),
              progressMessages.length - 1,
            );
            next.set(sessionId, {
              ...job,
              progress: newProgress,
              message: progressMessages[messageIndex],
            });
          }
          return next;
        });
      }, 2000);

      progressIntervalsRef.current.set(sessionId, interval);
    },
    [],
  );

  // ------ polling via setTimeout ------
  // We poll the server only to detect completion or failure.
  // Progress and messages are driven by client-side simulation above.

  const startPolling = useCallback(
    (sessionId: string) => {
      const POLL_INTERVAL = 2000;

      // Create an AbortController for this polling session
      const abortController = new AbortController();
      pollAbortControllersRef.current.set(sessionId, abortController);
      const signal = abortController.signal;

      const poll = async () => {
        // Check if aborted before making the request
        if (signal.aborted) return;

        try {
          const status = await api.getGenerationStatus(sessionId, signal);

          if (status.status === 'complete') {
            // Clear timers first to stop polling
            clearJobTimers(sessionId);

            // Update job status immediately so UI reflects completion
            // This ensures navigation happens even if getSession fails
            updateJob(sessionId, {
              status: 'complete',
              progress: 100,
              message: 'Generation complete!',
              result: null, // Will be populated if getSession succeeds
              completedAt: Date.now(),
            });

            // Try to fetch the full session data for caching (non-blocking)
            try {
              const fullSession = await api.getSession(sessionId);
              // Prime the TanStack Query cache so useSession can use it
              queryClient.setQueryData(queryKeys.session(sessionId), fullSession);
              // Update job with the full result
              updateJob(sessionId, { result: fullSession });
            } catch (err) {
              // Non-fatal: the job still completed, session can be fetched later
              if (!isAbortError(err)) {
                console.warn('[GenerationJobs] Failed to prefetch session data:', err);
              }
            }

            // Invalidate list so GenerationQueue refreshes
            queryClient.invalidateQueries({ queryKey: queryKeys.generationsList() });
            return; // stop chain
          }

          if (status.status === 'failed') {
            clearJobTimers(sessionId);
            updateJob(sessionId, {
              status: 'failed',
              error: status.error || 'Generation failed',
              completedAt: Date.now(),
            });
            queryClient.invalidateQueries({ queryKey: queryKeys.generationsList() });
            return; // stop chain
          }

          // Still in progress -- don't update progress/message from server,
          // the client-side simulation handles that.
          const tid = setTimeout(poll, POLL_INTERVAL);
          pollTimeoutsRef.current.set(sessionId, tid);
        } catch (error) {
          // If the request was aborted, stop the polling chain silently
          if (isAbortError(error)) return;

          console.error('[GenerationJobs] Poll error for', sessionId, error);
          // Retry with backoff on errors (4s, capped at 10s)
          const backoffDelay = Math.min(POLL_INTERVAL * 2, 10000);
          const tid = setTimeout(poll, backoffDelay);
          pollTimeoutsRef.current.set(sessionId, tid);
        }
      };

      // Fire first poll immediately
      poll();
    },
    [clearJobTimers, updateJob, queryClient],
  );

  // ------ Load stored generations on mount ------
  useEffect(() => {
    if (loadedRef.current) return;

    // Wait for authenticated user before loading generations.
    // Without this guard the effect fires on mount (before auth resolves,
    // when user?.id is undefined), sets loadedRef = true, and when auth
    // later resolves the user-change detection effect clears state but
    // the load never re-runs — leaving isLoadingJobs stuck at true.
    if (!user?.id) {
      setIsLoadingJobs(false);
      return;
    }

    loadedRef.current = true;
    setIsLoadingJobs(true);

    const abortController = new AbortController();
    loadAbortControllerRef.current = abortController;
    const signal = abortController.signal;

    // Helper to convert session list items to GenerationJob objects
    type SessionItem = Awaited<ReturnType<typeof api.listGenerations>>['sessions'][number];
    const sessionsToJobs = (sessions: SessionItem[]): { jobs: Map<string, GenerationJob>; inProgress: string[] } => {
      const jobsMap = new Map<string, GenerationJob>();
      const inProgress: string[] = [];

      for (const s of sessions) {
        if (s.status === 'complete') {
          jobsMap.set(s.sessionId, {
            id: s.sessionId,
            sessionId: s.sessionId,
            businessName: s.companyName,
            status: 'complete',
            progress: 100,
            message: 'Generation complete!',
            error: null,
            result: null, // Lazy load full session only when needed
            startedAt: new Date(s.createdAt).getTime(),
            completedAt: new Date(s.createdAt).getTime(),
          });
        } else if (s.status === 'failed') {
          jobsMap.set(s.sessionId, {
            id: s.sessionId,
            sessionId: s.sessionId,
            businessName: s.companyName,
            status: 'failed',
            progress: 0,
            message: '',
            error: 'Generation failed',
            result: null,
            startedAt: new Date(s.createdAt).getTime(),
            completedAt: new Date(s.createdAt).getTime(),
          });
        } else {
          // pending or generating
          jobsMap.set(s.sessionId, {
            id: s.sessionId,
            sessionId: s.sessionId,
            businessName: s.companyName,
            status: 'generating',
            progress: s.progress ?? 0,
            message: s.message || 'Generating...',
            error: null,
            result: null,
            startedAt: new Date(s.createdAt).getTime(),
            completedAt: null,
          });
          inProgress.push(s.sessionId);
        }
      }

      return { jobs: jobsMap, inProgress };
    };

    const load = async () => {
      try {
        // Progressive loading: fetch and display each page as it arrives
        let page = 1;
        let totalPages = 1;
        const allInProgress: string[] = [];

        do {
          if (signal.aborted) return;

          const { sessions, pagination } = await api.listGenerations({ page, limit: 100, signal });
          totalPages = pagination.totalPages;
          setTotalJobs(pagination.total);

          if (sessions.length === 0 && page === 1) {
            setIsLoadingJobs(false);
            return;
          }

          // Convert and immediately display this page's sessions
          const { jobs: pageJobs, inProgress } = sessionsToJobs(sessions);
          allInProgress.push(...inProgress);

          // Merge into state immediately so UI updates progressively
          setJobs((prev) => {
            const merged = new Map(prev);
            pageJobs.forEach((job, id) => {
              if (!merged.has(id)) merged.set(id, job);
            });
            return merged;
          });

          // After first page loads, hide the loading spinner
          if (page === 1) {
            setIsLoadingJobs(false);
          }

          page++;
        } while (page <= totalPages);

        if (signal.aborted) return;

        setHasMoreJobs(false);
        setCurrentPage(totalPages);

        // Resume progress simulation + polling for in-progress sessions
        for (const sessionId of allInProgress) {
          startProgressSimulation(sessionId);
          startPolling(sessionId);
        }
      } catch (error) {
        // If aborted during initial load, stop silently
        if (isAbortError(error)) return;
        console.error('[GenerationJobs] Failed to load stored generations:', error);
      } finally {
        if (!signal.aborted) {
          setIsLoadingJobs(false);
        }
      }
    };

    load();
  }, [startPolling, startProgressSimulation, queryClient, user?.id]);

  // ------ Cleanup on unmount: abort everything ------
  useEffect(() => {
    return () => {
      // Abort the initial load if it is still running
      loadAbortControllerRef.current?.abort();

      // Abort all polling controllers and clear all timers
      pollAbortControllersRef.current.forEach((controller) => controller.abort());
      pollAbortControllersRef.current.clear();

      pollTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      pollTimeoutsRef.current.clear();

      // Clear all progress simulation intervals
      progressIntervalsRef.current.forEach((interval) => clearInterval(interval));
      progressIntervalsRef.current.clear();
    };
  }, []);

  // ------ Reset state when user changes (login/logout/switch user) ------
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    const previousUserId = previousUserIdRef.current;

    // Skip on first render (handled by initial load effect)
    if (previousUserId === null && currentUserId !== null) {
      previousUserIdRef.current = currentUserId;
      return;
    }

    // Detect user change
    if (previousUserId !== currentUserId) {
      previousUserIdRef.current = currentUserId;

      // Abort any in-flight requests
      loadAbortControllerRef.current?.abort();
      pollAbortControllersRef.current.forEach((controller) => controller.abort());
      pollAbortControllersRef.current.clear();
      pollTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      pollTimeoutsRef.current.clear();
      progressIntervalsRef.current.forEach((interval) => clearInterval(interval));
      progressIntervalsRef.current.clear();

      // Clear all state
      setJobs(new Map());
      setActiveJobId(null);
      setCurrentPage(1);
      setTotalJobs(0);
      setHasMoreJobs(false);

      // Reset loadedRef so the initial load effect can run again
      loadedRef.current = false;

      // If user logged out, just mark as not loading
      if (!currentUserId) {
        setIsLoadingJobs(false);
        return;
      }

      // Trigger reload for new user by updating loading state
      // The initial load effect will run again since loadedRef.current is now false
      setIsLoadingJobs(true);
    }
  }, [user?.id]);

  // ------ startGeneration ------
  const startGeneration = useCallback(
    async (formData: FormData): Promise<string> => {
      const businessName = formData.business?.name || 'Unnamed Website';

      // Call the server to start generation
      const { sessionId } = await api.startGeneration(formData as any);

      const newJob: GenerationJob = {
        id: sessionId,
        sessionId,
        businessName,
        status: 'generating',
        progress: 0,
        message: 'Initializing generation...',
        error: null,
        result: null,
        startedAt: Date.now(),
        completedAt: null,
      };

      setJobs((prev) => {
        const next = new Map(prev);
        next.set(sessionId, newJob);
        return next;
      });

      setActiveJobId(sessionId);

      // Start client-side progress simulation + server polling
      startProgressSimulation(sessionId);
      startPolling(sessionId);

      // Invalidate the list cache
      queryClient.invalidateQueries({ queryKey: queryKeys.generationsList() });

      return sessionId;
    },
    [startPolling, startProgressSimulation, queryClient],
  );

  // ------ getJob ------
  const getJob = useCallback(
    (jobId: string): GenerationJob | undefined => jobs.get(jobId),
    [jobs],
  );

  // ------ removeJob ------
  const removeJob = useCallback(
    (jobId: string) => {
      clearJobTimers(jobId);
      setJobs((prev) => {
        const next = new Map(prev);
        next.delete(jobId);
        return next;
      });
      setActiveJobId((prev) => (prev === jobId ? null : prev));
    },
    [clearJobTimers],
  );

  // ------ cancelJob ------
  const cancelJob = useCallback(
    (jobId: string) => {
      clearJobTimers(jobId);

      const job = jobs.get(jobId);
      if (job?.sessionId) {
        api.cancelGeneration(job.sessionId).catch((err) => {
          // Don't log abort errors from cancel -- that is normal on unmount
          if (!isAbortError(err)) {
            console.error('[GenerationJobs] Failed to cancel on server:', err);
          }
        });
      }

      updateJob(jobId, {
        status: 'failed',
        error: 'Cancelled by user',
        completedAt: Date.now(),
      });
    },
    [clearJobTimers, updateJob, jobs],
  );

  // ------ setActiveJob ------
  const setActiveJob = useCallback((jobId: string | null) => {
    setActiveJobId(jobId);
  }, []);

  // ------ loadMoreJobs ------
  const loadMoreJobs = useCallback(async () => {
    if (isLoadingMore || !hasMoreJobs) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const { sessions, pagination } = await api.listGenerations({ page: nextPage, limit: 20 });

      setTotalJobs(pagination.total);
      setHasMoreJobs(pagination.page < pagination.totalPages);
      setCurrentPage(pagination.page);

      if (sessions.length === 0) {
        setIsLoadingMore(false);
        return;
      }

      const newJobs = new Map<string, GenerationJob>();

      for (const s of sessions) {
        if (s.status === 'complete') {
          let fullSession: GenerateResponse | null = null;
          try {
            fullSession = await api.getSession(s.sessionId);
            queryClient.setQueryData(queryKeys.session(s.sessionId), fullSession);
          } catch {
            // Non-fatal
          }
          newJobs.set(s.sessionId, {
            id: s.sessionId,
            sessionId: s.sessionId,
            businessName: s.companyName,
            status: 'complete',
            progress: 100,
            message: 'Generation complete!',
            error: null,
            result: fullSession,
            startedAt: new Date(s.createdAt).getTime(),
            completedAt: new Date(s.createdAt).getTime(),
          });
        } else if (s.status === 'failed') {
          newJobs.set(s.sessionId, {
            id: s.sessionId,
            sessionId: s.sessionId,
            businessName: s.companyName,
            status: 'failed',
            progress: 0,
            message: '',
            error: 'Generation failed',
            result: null,
            startedAt: new Date(s.createdAt).getTime(),
            completedAt: new Date(s.createdAt).getTime(),
          });
        } else {
          // pending or generating - these are rare in older pages but handle anyway
          newJobs.set(s.sessionId, {
            id: s.sessionId,
            sessionId: s.sessionId,
            businessName: s.companyName,
            status: 'generating',
            progress: s.progress ?? 0,
            message: s.message || 'Generating...',
            error: null,
            result: null,
            startedAt: new Date(s.createdAt).getTime(),
            completedAt: null,
          });
        }
      }

      setJobs((prev) => {
        const merged = new Map(prev);
        newJobs.forEach((job, id) => {
          if (!merged.has(id)) merged.set(id, job);
        });
        return merged;
      });
    } catch (error) {
      console.error('[GenerationJobs] Failed to load more generations:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMoreJobs, currentPage, queryClient]);

  const hasActiveJobs = jobs.size > 0;

  return (
    <GenerationJobsContext.Provider
      value={{
        jobs,
        activeJobId,
        isLoadingJobs,
        isLoadingMore,
        hasActiveJobs,
        hasMoreJobs,
        totalJobs,
        startGeneration,
        getJob,
        removeJob,
        cancelJob,
        setActiveJob,
        loadMoreJobs,
      }}
    >
      {children}
    </GenerationJobsContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useGenerationJobs(): GenerationJobsContextType {
  const ctx = useContext(GenerationJobsContext);
  if (!ctx) {
    throw new Error('useGenerationJobs must be used within a GenerationJobsProvider');
  }
  return ctx;
}

export default GenerationJobsContext;
